import React, { useState, useMemo, useEffect, useRef, useContext } from 'react';
import FilterFixedHeader from '../FilterFixedHeader';
import FilterBox from '../FilterBox';
import useFilterFixed from '../../hooks/useFilterFixed';
import type { FilterCommonType, OperateShowItemType } from '../../types';
import { connect, getLocale, useDispatch } from 'umi';
import { LocaleType } from '@/pages/opinion/types/common';
import { FilterGameInfoContext } from '../../context';
import { useSyncState } from '@op/hooks/common';
import './index.scss';
import { SAVE_PAGE_NAME } from '@op/components/GPT/constants';

function FilterFixed({
  scrollBody,
  confirm,
  onChange,
  operateShowOptions = ['tips', 'save', 'more', 'history'],
  localGameInfo,
  localWholeGameInfo,
  userInfo,
  noLimitation = false,
  isAccountRegion = false,
  styles = {},
  hideTop,
  pageName,
  renderExcludeFilter,
  ...otherProps
}: Omit<FilterCommonType, 'tips' | 'lang'> & {
  /** 控制是否显示浮层 */
  scrollBody?: React.MutableRefObject<any>;
  localGameInfo: any;
  localWholeGameInfo: any;
  userInfo: any;
  styles?: React.CSSProperties;
  hideTop?: number;
}) {
  const locale: LocaleType = getLocale();
  const [selectShow, setSelectShow] = useState(false);
  const noChangeFilterHeight = useRef(false);
  const dispatch = useDispatch();
  const [filterHeight, setFilterHeight] = useState(0);
  const { filterFixed } = useFilterFixed({
    scrollBody,
    minHeight: filterHeight,
  });

  const {
    state: showMore,
    setState: setShowMore,
    initStates: initShowMore,
    previousState: preShowMore,
    revertToPrevious,
  } = useSyncState(false);

  const {
    gameInfo: contentGameInfo,
    use,
    wholeGameInfo: contentWholeGameInfo,
  } = useContext(FilterGameInfoContext);

  const { gameInfo, wholeGameInfo } = useMemo(() => {
    if (use) {
      return {
        gameInfo: contentGameInfo,
        wholeGameInfo: contentWholeGameInfo,
      };
    }
    return {
      gameInfo: localGameInfo,
      wholeGameInfo: localWholeGameInfo,
    };
  }, [localGameInfo, contentGameInfo]);

  const className = useMemo(() => {
    let text = 'dbo-filterfixed-wrapper';
    if (filterFixed) {
      text += ' dbo-filterbox-fixed filter-visiable';
    }

    if (selectShow) {
      text += ' filter-expand';
    }
    return text;
  }, [filterFixed, selectShow]);

  const toggle = (isHeaderChange?: boolean) => {
    const value = !selectShow;

    if (isHeaderChange) {
      noChangeFilterHeight.current = value;
    }

    if (value) setShowMore(true);
    setTimeout(() => {
      setSelectShow(value);
    }, 100);
  };

  function toggleShowMore() {
    initShowMore(!preShowMore);
  }

  const close = (isHeaderChange?: boolean) => { 
    if (isHeaderChange) {
      noChangeFilterHeight.current = false;
    }

    revertToPrevious();
    setSelectShow(false);
  };

  const operateOptions: OperateShowItemType[] = useMemo(() => {
    if (operateShowOptions === null) return [];
    return operateShowOptions;
  }, []);

  const ref = useRef<HTMLDivElement>(null);
  const [minHeight, setMinHeight] = useState(0);

  const [renderFixedHeader, setRenderFixedHeader] = useState({});
  // undefined 为初始化， 空字符串【""】, 后期设置，主要为了默认选中指定过滤器
  const [filterName, setFilterName] = useState<string | undefined>(undefined);

  // 触发confirm操作
  const confirmData = (
    isBtnConfirm = false,
    value: any,
    oriFilterValue: any,
  ) => {
    if (isBtnConfirm) {
      if (pageName) {
        // 将数据传入到reduce，后续可以生成shortId进行保存
        dispatch({
          type: 'opinionAIShortIdFilter/save',
          payload: { ...oriFilterValue, [SAVE_PAGE_NAME]: pageName },
        });
      }
      confirm?.(value, oriFilterValue);
      close(true);
    }
  };

  const backConfirm = (
    { value, fixedHeader, oriFilterValue }: any,
    isBtnConfirm = false,
  ) => {
    confirmData(isBtnConfirm, value, oriFilterValue);
    // setFilterToUrl(value, fixedHeader);
    onChange?.(value, fixedHeader);
    setRenderFixedHeader(fixedHeader);
  };

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const targetElement = entry.target as HTMLElement;

        const height = targetElement.offsetHeight || 0;
        const top = targetElement.offsetTop || 0;
        const minHeight = height + top;
        if (noChangeFilterHeight.current) {
          return;
        }
        if (minHeight > 0) {
          setMinHeight(height);
          setFilterHeight(minHeight); // Update height
        }
      }
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.unobserve(element);
    };
  }, [selectShow]);

  return (
    <div className="dbo-filterbox" style={{ minHeight, ...styles }}>
      <div className={className} ref={ref}>
        <FilterFixedHeader
          filterName={filterName}
          filter={otherProps.filter}
          renderExcludeFilter={renderExcludeFilter}
          lang={locale}
          isAccountRegion={isAccountRegion}
          showFilterText={operateOptions.includes('history')}
          renderFixedHeader={renderFixedHeader}
          change={() => toggle(true)}
        />

        <div className={filterFixed ? 'dbo-filterbox-header' : ''}>
          <FilterBox
            userInfo={userInfo}
            gameInfo={gameInfo}
            wholeGameInfo={wholeGameInfo}
            {...otherProps}
            renderExcludeFilter={renderExcludeFilter}
            lang={locale}
            isAccountRegion={isAccountRegion}
            operateOptions={operateOptions}
            confirm={backConfirm}
            filterFixed={filterFixed}
            showMore={showMore}
            toggleShowMore={toggleShowMore}
            noLimitation={noLimitation}
            close={() => close(true)}
            filterName={filterName}
            setFilterName={setFilterName}
          ></FilterBox>
        </div>
      </div>
    </div>
  );
}

export default connect((state: any) => {
  const { home, opinionCommon } = state;
  return {
    userInfo: home.userInfo,
    localGameInfo: opinionCommon.gameInfo,
    localWholeGameInfo: opinionCommon.wholeGameInfo,
  };
})(FilterFixed);
